import {
  IRuleGroupDeleteRequest,
  IRuleGroupDeleteResponse,
  IRuleGroupUpdateRequest,
  IRuleGroupUpdateResponse,
} from "types/AdjustmentRules/AdjustmentRules";
import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IProcedureDiscountRuleset,
  IProcedureDiscountRulesetCreateRequest,
  IProcedureDiscountRulesetGroupData,
  IProcedureDiscountRulesetUpdateRequest,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRuleset";
import {
  IAllProcedureDiscountRulesetResponse,
  IProcedureDiscountRulesetResponse,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRulesetResponse";

import { api } from ".";

const procedureDiscountRulesetQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllDiscountRulesets: builder.query<
      IAllProcedureDiscountRulesetResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `payer-rules/all${search}`;
      },
      providesTags: ["MpprRulesets"],
    }),
    getDiscountRulesetById: builder.query<
      IProcedureDiscountRulesetResponse,
      string | undefined
    >({
      query: (id: string) => `payer-rules/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "MpprRulesets", id: id ?? "LIST" },
      ],
    }),
    createDiscountRuleset: builder.mutation<
      IProcedureDiscountRuleset,
      IProcedureDiscountRulesetCreateRequest
    >({
      query: (newRuleset) => ({
        url: "payer-rules",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["MpprRulesets", "ContractFeeSchedules"],
    }),
    updateDiscountRuleset: builder.mutation<
      IProcedureDiscountRuleset,
      IProcedureDiscountRulesetUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `payer-rules/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: [
        "MpprRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    // CLE-2842 — edits every row of a bucket (same rule, same provider, different payers) in
    // one server transaction; never emulate it with a fan-out of single-row PUTs.
    updateDiscountRulesetGroup: builder.mutation<
      IRuleGroupUpdateResponse<IProcedureDiscountRuleset>,
      IRuleGroupUpdateRequest<IProcedureDiscountRulesetGroupData>
    >({
      query: (body) => ({
        url: "payer-rules/group",
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "MpprRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    // CLE-2842 — deletes every row of a bucket in one server transaction (409 when a listed row
    // is missing or drifted, with nothing deleted); never emulate it with single DELETEs.
    deleteDiscountRulesetGroup: builder.mutation<
      IRuleGroupDeleteResponse,
      IRuleGroupDeleteRequest
    >({
      query: (body) => ({
        url: "payer-rules/group/delete",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "MpprRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    deleteDiscountRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `payer-rules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "MpprRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    cloneDiscountRulesetAsDefault: builder.mutation<
      IProcedureDiscountRulesetResponse,
      string
    >({
      query: (id) => ({
        url: `payer-rules/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["MpprRulesets"],
    }),
    copyDiscountRulesetToContract: builder.mutation<
      IProcedureDiscountRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `payer-rules/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MpprRulesets", "ContractFeeSchedules"],
    }),
    getDiscountRulesetPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `payer-rules/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `mppr:${id}` },
      ],
    }),
    applyDiscountRulesetPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `payer-rules/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "MpprRulesets",
        "ContractFeeSchedules",
        { type: "DefaultRulePropagation", id: `mppr:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllDiscountRulesetsQuery,
  useGetDiscountRulesetByIdQuery,
  useCreateDiscountRulesetMutation,
  useUpdateDiscountRulesetMutation,
  useUpdateDiscountRulesetGroupMutation,
  useDeleteDiscountRulesetMutation,
  useDeleteDiscountRulesetGroupMutation,
  useCloneDiscountRulesetAsDefaultMutation,
  useCopyDiscountRulesetToContractMutation,
  useGetDiscountRulesetPropagationPreviewQuery,
  useLazyGetDiscountRulesetPropagationPreviewQuery,
  useApplyDiscountRulesetPropagationMutation,
} = procedureDiscountRulesetQueries;
